import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaCheckCircle, FaWallet, FaUsers, FaFileInvoice, FaArrowCircleUp } from 'react-icons/fa';

interface Plan {
  id: number;
  name: string;
  price: number;
  duration_months: number;
  max_invoices: number;
  max_users: number;
}

interface Company {
  id: number;
  company_name: string;
  subscription_plan_id: number | null;
  subscription_start_date: string | null;
  subscription_status: string;
  subscription_plan_name?: string;
  max_invoices?: number;
  max_users?: number;
  email?: string;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

function decodeToken(token: string) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {};
  }
}

export default function SubscriptionBilling() {
  const [company, setCompany] = useState<Company | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState<number | null>(null);

  const API_URL = process.env.REACT_APP_API_URL;

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Session token missing. Please log in.');
        setLoading(false);
        return;
      }

      const decoded = decodeToken(token);
      const userCompanyId = decoded.userId || decoded.id;

      // 1. Fetch Company Info
      const compRes = await axios.get(`${API_URL}/auth/companies`);
      const companiesList = Array.isArray(compRes.data) ? compRes.data : [];
      
      let matchedCompany = companiesList.find((c: any) => String(c.id) === String(userCompanyId));
      if (!matchedCompany && companiesList.length > 0) {
        // Fallback to currently active workspace
        const activeWorkspaceId = localStorage.getItem('selected_company_id');
        if (activeWorkspaceId && activeWorkspaceId !== 'all') {
          matchedCompany = companiesList.find((c: any) => String(c.id) === String(activeWorkspaceId));
        } else {
          matchedCompany = companiesList[0];
        }
      }

      if (matchedCompany) {
        setCompany(matchedCompany);
      }

      // 2. Fetch Available Plans
      const plansRes = await axios.get(`${API_URL}/auth/subscription-plans`);
      setPlans(Array.isArray(plansRes.data) ? plansRes.data : []);

      // 3. Calculate usage stats (Invoices generated this month)
      if (matchedCompany) {
        const invoicesRes = await axios.get(`${API_URL}/auth/invoices`);
        const purchaseRes = await axios.get(`${API_URL}/auth/purchase-invoices`);
        const salesInvoices = Array.isArray(invoicesRes.data) ? invoicesRes.data : [];
        const purchaseInvoices = Array.isArray(purchaseRes.data) ? purchaseRes.data : [];

        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        const currentMonthSales = salesInvoices.filter((inv: any) => new Date(inv.Date) >= startOfMonth).length;
        const currentMonthPurchases = purchaseInvoices.filter((inv: any) => new Date(inv.Date) >= startOfMonth).length;
        setInvoiceCount(currentMonthSales + currentMonthPurchases);

        // 4. Calculate User Count
        const usersRes = await axios.get(`${API_URL}/auth/allusersinfo`);
        const usersList = Array.isArray(usersRes.data) ? usersRes.data : [];
        const companyUsers = usersList.filter((u: any) => String(u.company_id) === String(matchedCompany.id));
        setUserCount(companyUsers.length || 1); // fallback to 1 (main admin)
      }
    } catch (err: any) {
      console.error('Error fetching billing data:', err);
      setError('Failed to retrieve billing and workspace statistics.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCheckout = async (plan: Plan) => {
    setCheckoutLoading(plan.id);
    setError('');
    setSuccess('');
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setError('Failed to load Razorpay payment gateway. Please check your internet connection.');
        setCheckoutLoading(null);
        return;
      }

      // Create order
      const orderRes = await axios.post(`${API_URL}/auth/subscription/create-order`, {
        plan_id: plan.id
      });

      const { order_id, amount, currency, key_id } = orderRes.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'Invoice Workspace Billing',
        description: `Upgrade to ${plan.name}`,
        image: 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png',
        order_id: order_id,
        handler: async (response: any) => {
          setLoading(true);
          try {
            const verifyRes = await axios.post(`${API_URL}/auth/subscription/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id || order_id,
              razorpay_payment_id: response.razorpay_payment_id || `pay_mock_${Math.random().toString(36).substring(2, 10)}`,
              razorpay_signature: response.razorpay_signature || '',
              plan_id: plan.id
            });
            setSuccess(verifyRes.data.message || 'Subscription upgraded successfully!');
            await fetchData();
          } catch (verErr: any) {
            setError(verErr.response?.data?.error || 'Payment verification failed.');
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: company?.company_name || 'Valued Partner',
          email: company?.email || 'billing@workspace.com'
        },
        theme: {
          color: '#6366f1'
        },
        modal: {
          ondismiss: () => {
            setCheckoutLoading(null);
          }
        }
      };

      // Check if order is a mock order (i.e. credentials are not set on backend)
      if (order_id.startsWith('order_mock_')) {
        // Auto-complete payment for test/demo ease
        console.log('Using mock mode. Simulating successful checkout...');
        setTimeout(async () => {
          try {
            const verifyRes = await axios.post(`${API_URL}/auth/subscription/verify-payment`, {
              razorpay_order_id: order_id,
              razorpay_payment_id: `pay_mock_${Math.random().toString(36).substring(2, 10)}`,
              razorpay_signature: '',
              plan_id: plan.id
            });
            setSuccess(verifyRes.data.message || 'Demo Subscription upgraded successfully!');
            await fetchData();
          } catch (verErr: any) {
            setError(verErr.response?.data?.error || 'Mock Payment verification failed.');
          } finally {
            setCheckoutLoading(null);
          }
        }, 1500);
        return;
      }

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.error || 'Failed to initiate billing session.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] gap-3">
        <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
        <p className="text-gray-500 font-semibold">Loading billing panel...</p>
      </div>
    );
  }

  const currentPlanId = company?.subscription_plan_id;
  const planInvoicesLimit = company?.max_invoices || 5;
  const planUsersLimit = company?.max_users || 2;
  const subStatus = company?.subscription_status || 'inactive';
  const subStartDate = company?.subscription_start_date;

  const invoicePercent = Math.min(100, Math.round((invoiceCount / planInvoicesLimit) * 100));
  const userPercent = Math.min(100, Math.round((userCount / planUsersLimit) * 100));

  return (
    <div className="max-w-6xl mx-auto py-10 px-6 font-sans">
      {/* Header */}
      <div className="mb-10 text-center md:text-left flex flex-col md:flex-row justify-between items-center gap-6">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight flex items-center justify-center md:justify-start gap-2.5">
            <FaWallet className="text-indigo-600 text-2xl" /> Billing & Subscription
          </h2>
          <p className="text-gray-500 mt-1.5">Manage billing details, track workspace usage limits, and unlock premium tiers.</p>
        </div>
        <div className="bg-white border border-slate-100 shadow-sm rounded-2xl px-5 py-3.5 flex items-center gap-3">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Workspace:</span>
          <strong className="text-slate-900 font-bold text-sm">{company?.company_name}</strong>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-2xl border border-red-200 font-semibold text-sm shadow-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-emerald-50 text-emerald-800 rounded-2xl border border-emerald-200 font-semibold text-sm shadow-sm flex items-center gap-2">
          <FaCheckCircle className="text-emerald-600" /> {success}
        </div>
      )}

      {/* Grid: Current Plan Details & Usage */}
      <div className="grid lg:grid-cols-3 gap-8 mb-12">
        {/* Active Plan Detail */}
        <div className="bg-gradient-to-br from-indigo-900 via-indigo-950 to-indigo-900 text-white rounded-3xl p-8 shadow-xl relative overflow-hidden flex flex-col justify-between min-h-[300px]">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl"></div>
          <div>
            <span className="text-xs font-bold text-indigo-200/80 uppercase tracking-widest bg-indigo-800/50 px-3 py-1.5 rounded-full border border-indigo-700/30">
              Active Tier
            </span>
            <h3 className="text-2xl font-black mt-4 tracking-tight">{company?.subscription_plan_name || 'Free Plan'}</h3>
            <p className="text-indigo-200/70 text-xs mt-2.5 font-medium">
              Start Date: {subStartDate ? new Date(subStartDate).toLocaleDateString() : 'N/A'}
            </p>
          </div>

          <div className="mt-8 border-t border-indigo-800/80 pt-6">
            <div className="flex justify-between items-center">
              <span className="text-xs font-bold text-indigo-300 uppercase tracking-wider">Status:</span>
              <span className={`text-xs font-extrabold px-3 py-1.5 rounded-xl uppercase tracking-wider shadow ${
                subStatus === 'active' ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'
              }`}>
                {subStatus}
              </span>
            </div>
          </div>
        </div>

        {/* Invoice Usage */}
        <div className="bg-white border border-slate-100 shadow-lg rounded-3xl p-8 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                <FaFileInvoice className="text-lg" />
              </div>
              <div>
                <h4 className="font-extrabold text-slate-800 text-sm">Monthly Invoices</h4>
                <p className="text-gray-400 text-xs">Total sales + purchase bills</p>
              </div>
            </div>

            <div className="flex items-baseline gap-1 mt-6">
              <span className="text-3xl font-black text-slate-900">{invoiceCount}</span>
              <span className="text-slate-400 text-sm font-semibold">/ {planInvoicesLimit >= 999999 ? 'Unlimited' : planInvoicesLimit}</span>
            </div>
          </div>

          <div className="mt-8">
            <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
              <div 
                className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${invoicePercent}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-[11px] font-bold text-slate-500 mt-2.5">
              <span>{invoicePercent}% Consumed</span>
              <span>{planInvoicesLimit - invoiceCount} remaining</span>
            </div>
          </div>
        </div>

        {/* User Usage */}
        <div className="bg-white border border-slate-100 shadow-lg rounded-3xl p-8 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                <FaUsers className="text-lg" />
              </div>
              <div>
                <h4 className="font-extrabold text-slate-800 text-sm">Member Accounts</h4>
                <p className="text-gray-400 text-xs">Registered team members</p>
              </div>
            </div>

            <div className="flex items-baseline gap-1 mt-6">
              <span className="text-3xl font-black text-slate-900">{userCount}</span>
              <span className="text-slate-400 text-sm font-semibold">/ {planUsersLimit >= 999 ? 'Unlimited' : planUsersLimit}</span>
            </div>
          </div>

          <div className="mt-8">
            <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
              <div 
                className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${userPercent}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-[11px] font-bold text-slate-500 mt-2.5">
              <span>{userPercent}% Consumed</span>
              <span>{planUsersLimit - userCount} remaining</span>
            </div>
          </div>
        </div>
      </div>

      {/* Section: Subscription Plans */}
      <div>
        <h3 className="text-2xl font-extrabold text-slate-800 mb-8 text-center tracking-tight">Select Premium Workspace Tier</h3>
        
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan) => {
            const isActive = currentPlanId === plan.id;
            const isFree = plan.price === 0;

            return (
              <div 
                key={plan.id} 
                className={`bg-white rounded-3xl border transition-all duration-300 p-8 shadow-md flex flex-col justify-between relative ${
                  isActive 
                    ? 'border-indigo-600 ring-4 ring-indigo-100 scale-105' 
                    : 'border-slate-100 hover:border-indigo-300 hover:shadow-xl'
                }`}
              >
                {isActive && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow">
                    Current Workspace Tier
                  </span>
                )}

                <div>
                  <h4 className="text-lg font-black text-slate-900 mt-2">{plan.name}</h4>
                  
                  <div className="flex items-baseline gap-1.5 mt-4">
                    <span className="text-3xl font-black text-slate-950">₹{plan.price}</span>
                    <span className="text-slate-400 text-xs font-semibold">/ {plan.duration_months} month{plan.duration_months > 1 ? 's' : ''}</span>
                  </div>

                  <ul className="mt-8 space-y-4 text-xs font-semibold text-slate-600 border-t border-slate-100 pt-6">
                    <li className="flex items-center gap-2.5">
                      <FaCheckCircle className="text-indigo-500 text-sm flex-shrink-0" />
                      <span>{plan.max_invoices >= 999999 ? 'Unlimited' : `${plan.max_invoices} Monthly Invoices`}</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <FaCheckCircle className="text-indigo-500 text-sm flex-shrink-0" />
                      <span>{plan.max_users >= 999 ? 'Unlimited' : `${plan.max_users} Team Members`}</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <FaCheckCircle className="text-indigo-500 text-sm flex-shrink-0" />
                      <span>Instant Invoice PDF Generation</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <FaCheckCircle className="text-indigo-500 text-sm flex-shrink-0" />
                      <span>Multi-Bank Details Setup</span>
                    </li>
                  </ul>
                </div>

                <div className="mt-8 pt-4">
                  {isActive ? (
                    <button 
                      disabled
                      className="w-full bg-slate-100 text-slate-400 font-extrabold text-xs py-3.5 px-4 rounded-xl cursor-not-allowed uppercase tracking-wider transition"
                    >
                      Active workspace
                    </button>
                  ) : (
                    <button
                      onClick={() => handleCheckout(plan)}
                      disabled={checkoutLoading !== null}
                      className="w-full bg-indigo-600 text-white hover:bg-indigo-700 font-extrabold text-xs py-3.5 px-4 rounded-xl shadow uppercase tracking-wider flex items-center justify-center gap-2 transition disabled:opacity-50"
                    >
                      {checkoutLoading === plan.id ? (
                        <span className="w-4 h-4 border-2 border-indigo-200 border-t-white rounded-full animate-spin"></span>
                      ) : (
                        <>
                          <FaArrowCircleUp className="text-sm" /> Subscribe Now
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
